'use client';
import { useTranslations } from 'next-intl';
import type { ElasticVehicleDocument } from '@//app/api/search/vehicles/[id]';
import { Alert } from '@//components/Alert';
import { DocumentList } from '@//components/DocumentList';
import { Heading } from '@//components/Heading';
import { TextContent } from '@//components/TextContent';
import styles from './Documents.module.scss';

const contentTypeLabels: Record<string, { label: string; ext: string }> = {
  'application/pdf': { label: 'PDF', ext: '.pdf' },
  'image/jpeg': { label: 'JPEG', ext: '.jpg' }, // or ".jpeg", adapt as needed
  'image/png': { label: 'PNG', ext: '.png' },
  'image/svg+xml': { label: 'SVG', ext: '.svg' },
};

const resolveContentType = (contentType: string) =>
  contentTypeLabels[contentType]?.label || contentType;

const trimExtension = (name: string, contentType: string): string => {
  const entry = contentTypeLabels[contentType];
  if (!entry) return name;
  // Case insensitive match and removal
  if (name.toLowerCase().endsWith(entry.ext)) {
    return name.slice(0, -entry.ext.length);
  }
  // For JPEGs, handle both ".jpg" and ".jpeg"
  if (contentType === 'image/jpeg' && name.toLowerCase().endsWith('.jpeg')) {
    return name.slice(0, -'.jpeg'.length);
  }
  return name;
};

interface DocumentsProps {
  documents?: ElasticVehicleDocument[] | null;
}

export const Documents: React.FC<DocumentsProps> = ({ documents }) => {
  const t = useTranslations('pages.item.moreInformationSection.documents');

  // Categorize documents in a single pass
  const { tireDocuments, contentDeclarationDocuments, otherDocuments } = (documents || []).reduce(
    (acc, doc) => {
      switch (doc.category) {
        case 'tyre-label':
          acc.tireDocuments.push(doc);
          break;
        case 'content declaration':
          acc.contentDeclarationDocuments.push(doc);
          break;
        default:
          acc.otherDocuments.push(doc);
      }
      return acc;
    },
    {
      tireDocuments: [] as ElasticVehicleDocument[],
      contentDeclarationDocuments: [] as ElasticVehicleDocument[],
      otherDocuments: [] as ElasticVehicleDocument[],
    },
  );

  // Helper to map documents for DocumentList
  const mapForList = (docs: ElasticVehicleDocument[]) =>
    docs.map((doc) => ({
      format: resolveContentType(doc.contentType),
      title: trimExtension(doc.name, doc.contentType),
      url: doc.url,
    }));

  const CONTENT_DECLARATION_HEADING_ID = 'document-content-declaration-heading';
  const TIRE_HEADING_ID = 'document-tire-heading';
  const OTHER_HEADING_ID = 'document-other-heading';

  return (
    <>
      {documents?.length ? (
        <div className={styles.content}>
          {!!contentDeclarationDocuments.length && (
            <section className={styles.section} aria-labelledby={CONTENT_DECLARATION_HEADING_ID}>
              <header className={styles.sectionHeader}>
                <Heading level={3} size="lg" id={CONTENT_DECLARATION_HEADING_ID}>
                  {t('contentDeclaration.heading')}
                </Heading>
                <TextContent textSize="md">
                  <p>{t('contentDeclaration.description')}</p>
                </TextContent>
              </header>
              <DocumentList documents={mapForList(contentDeclarationDocuments)} />
            </section>
          )}
          {!!tireDocuments.length && (
            <section className={styles.section} aria-labelledby={TIRE_HEADING_ID}>
              <Heading level={3} size="lg" id={TIRE_HEADING_ID}>
                {t('tires.heading')}
              </Heading>
              <DocumentList documents={mapForList(tireDocuments)} />
            </section>
          )}
          {!!otherDocuments.length && (
            <section className={styles.section} aria-labelledby={OTHER_HEADING_ID}>
              <header className={styles.sectionHeader}>
                <Heading level={3} size="lg" id={OTHER_HEADING_ID}>
                  {t('other.heading')}
                </Heading>
              </header>
              <DocumentList documents={mapForList(otherDocuments)} />
            </section>
          )}
        </div>
      ) : (
        <Alert id="alert-no-documents" variant="info">
          {t('noDocuments')}
        </Alert>
      )}
    </>
  );
};
